import React, { createContext, useReducer, useCallback, useEffect } from 'react';
import userReducer from './AuthReducer.js';
import * as types from './authActionTypes.js';
import mernDashApi from '../../helpers/apiUtils.js';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
import decode from 'jwt-decode';

const initialAuthState = {
  loading: false,
  error: false,
  token: null,
  errResponse: null,
  // ── NEW: subscription fields ──────────────────────────────────────────────
  subscriptionStatus: localStorage.getItem('subscriptionStatus') || 'active',
  subscriptionCode:   localStorage.getItem('subscriptionCode')   || null,
};

export const AuthContext = createContext(initialAuthState);

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(userReducer, initialAuthState);
  const navigate = useNavigate();

  const AuthReset = useCallback(() => {
    dispatch({ type: types.AUTH_RESET });
  }, []);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      try {
        const { exp } = decode(storedToken);
        if (!exp || exp * 1000 <= Date.now()) throw new Error('Expired token');
        dispatch({ type: types.AUTH_SUCCESS, payload: storedToken });
      } catch {
        localStorage.removeItem('token');
        dispatch({ type: types.AUTH_RESET });
      }
    }
  }, []);

  const LoginAction = useCallback(async (data) => {
    dispatch({ type: types.AUTH_START });

    try {
      const res = await mernDashApi.post('/api/auth/login', data);

      // ── NEW: also destructure subscriptionStatus and subscriptionCode ──────
      const {
        access_token,
        username,
        email,
        profileImage,
        roles,
        subscriptionStatus = 'active',
        subscriptionCode   = null,
      } = res.data;

      if (!access_token) {
        throw new Error('No access_token in response');
      }

      const decoded = decode(access_token);
      const userRole = decoded.role || roles;
      const normalizedRoles = Array.isArray(userRole) ? userRole : [userRole];

      if (!normalizedRoles.some(role => ['user', 'admin', 'superadmin'].includes(role))) {
        throw new Error('Invalid role: ' + normalizedRoles.join(', '));
      }

      localStorage.setItem('token',               access_token);
      localStorage.setItem('username',            username);
      localStorage.setItem('email',               email);
      localStorage.setItem('profileImage',        profileImage || '');
      localStorage.setItem('roles',               JSON.stringify(normalizedRoles));
      // ── NEW: save subscription status to localStorage ─────────────────────
      localStorage.setItem('subscriptionStatus',  subscriptionStatus);
      localStorage.setItem('subscriptionCode',    subscriptionCode || '');

      // ── NEW: dispatch subscription info alongside token ───────────────────
      dispatch({
        type: types.AUTH_SUCCESS,
        payload: {
          token:              access_token,
          subscriptionStatus,
          subscriptionCode,
        },
      });

      message.success('Login successful!', 3);

      // ── NEW: show warning toast if subscription is paused or cancelled ─────
      // (login still succeeds — user lands on dashboard and sees the banner)
      if (subscriptionStatus === 'paused') {
        message.warning(
          'Your subscription is paused. Please renew to restore full access.',
          8
        );
      } else if (subscriptionStatus === 'cancelled') {
        message.error(
          'Your subscription has been cancelled. Please reactivate your plan.',
          8
        );
      }

      navigate(
        normalizedRoles.includes('superadmin') ? '/createuser' : '/dashboard',
        { replace: true }
      );

    } catch (error) {
      console.log('Login error:', error.response?.data);

      // Generic error handling (wrong password, unknown email, server errors, etc.)
      let errorMessage = 'Login failed due to an unexpected error';
      if (error.response?.data) {
        const errors = error.response.data.error_msg || error.response.data;
        if (Array.isArray(errors)) {
          errorMessage = errors.map(err => err.replace('error: ', '')).join(', ');
        } else if (typeof errors === 'string') {
          errorMessage = errors.replace('error: ', '');
          errorMessage = errorMessage === 'invalid password' ? 'Incorrect password'
                       : errorMessage === 'invalid email'   ? 'Incorrect email ID'
                       : errorMessage;
        } else if (typeof errors === 'object') {
          if (errors.password)   errorMessage = 'Incorrect password';
          else if (errors.email) errorMessage = 'Incorrect email ID';
          else errorMessage = Object.values(errors).map(err => err.replace('error: ', '')).join(', ');
        }
      }

      dispatch({ type: types.AUTH_FAILURE, payload: errorMessage });
      message.error(errorMessage, 3);

      localStorage.clear();
      navigate('/user/login', { replace: true });
    }
  }, [navigate]);

  const ForgotPasswordAction = useCallback(async (data) => {
    dispatch({ type: types.AUTH_START });

    try {
      const res = await mernDashApi.post('/api/auth/forgot-password', data);
      // console.log('ForgotPasswordAction: Email sent response:', res.data);

      dispatch({ type: types.AUTH_SUCCESS, payload: { token: null } });
      message.success('Reset password link sent to your email.', 3);
    } catch (error) {
      const errorMessage = error.response?.data?.error_msg || 'Failed to send reset email';
      dispatch({
        type: types.AUTH_FAILURE,
        payload: errorMessage.replace('error: ', ''),
      });
      message.error(errorMessage.replace('error: ', ''), 3);
    }
  }, []);

  const ChangePasswordAction = useCallback(async (data) => {
    dispatch({ type: types.AUTH_START });

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No token found, please log in again');

      const res = await mernDashApi.post('/api/auth/change-password', data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const { access_token, username, email, profileImage, roles } = res.data;

      if (!access_token) throw new Error('No access_token in response');

      const decoded = decode(access_token);
      const userRole = decoded.role || roles;
      const normalizedRoles = Array.isArray(userRole) ? userRole : [userRole];

      localStorage.setItem('token',        access_token);
      localStorage.setItem('username',     username);
      localStorage.setItem('email',        email);
      localStorage.setItem('profileImage', profileImage || '');
      localStorage.setItem('roles',        JSON.stringify(normalizedRoles));

      dispatch({ type: types.AUTH_SUCCESS, payload: { token: access_token } });

      navigate(
        normalizedRoles.includes('superadmin') ? '/createuser' : '/dashboard',
        { replace: true }
      );

    } catch (error) {
      const errorMessage = error.response?.data?.error_msg || 'Password reset failed';
      dispatch({
        type: types.AUTH_FAILURE,
        payload: errorMessage.replace('error: ', ''),
      });
      message.error(errorMessage.replace('error: ', ''), 3);

      if (error.response?.status === 401) {
        localStorage.clear();
        navigate('/user/login', { replace: true });
      }
    }
  }, [navigate]);

  return (
    <AuthContext.Provider
      value={{
        state,
        AuthReset,
        LoginAction,
        ForgotPasswordAction,
        ChangePasswordAction,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
